import { useEffect, type Dispatch, type SetStateAction } from 'react';
import useLeftHandLayout, {
	type Path,
	type LeftHandLayoutBreadcrumbs,
} from './context';

// Type mapping for setter keys to their corresponding value types
interface SetterKeyToValueType {
	setTitle: string;
	setBreadcrumbs: LeftHandLayoutBreadcrumbs[] | undefined;
	setPath: Path | undefined;
	setLeftSize: number;
	setTopSize: number;
	setTopInnerX: number;
	setTopOuterY: number;
}

/** Create a setter hook  */
export function createNavSetter<K extends keyof SetterKeyToValueType>(
	setterKey: K,
	defaultValue: SetterKeyToValueType[K],
) {
	return (value: SetterKeyToValueType[K]) => {
		const context = useLeftHandLayout();
		const setter = context[setterKey] as Dispatch<
			SetStateAction<SetterKeyToValueType[K]>
		>;

		useEffect(() => {
			setter(value);

			return () => {
				setter((current: SetterKeyToValueType[K]) => {
					// Only clear if it is what we set
					if (current !== value) return current;
					return defaultValue;
				});
			};
		}, [value, setter]);
	};
}

/** Set the nav title */
export const useNavTitle = (title: string) => {
	const { setTitle } = useLeftHandLayout();

	useEffect(() => {
		setTitle(title);

		return () => {
			setTitle((current) => {
				// Only clear if it is what we set
				if (current !== title) return current;
				return '';
			});
		};
	}, [setTitle, title]);
};

/**
 * Set the nav breadcrumbs
 */
export const useNavBreadcrumbs = createNavSetter('setBreadcrumbs', []);

/**
 * Override the active path in the nav sidebar
 */
export const useNavActivePath = createNavSetter('setPath', undefined);

/**
 * Set the nav leftSize
 * @private For layout internal use
 */
export const useNavLeftSize = createNavSetter('setLeftSize', 0);

/**
 * Set the nav topSize
 * @private For layout internal use
 */
export const useNavTopSize = createNavSetter('setTopSize', 0);

/**
 * Set the nav topInnerX
 * @private For layout internal use
 */
export const useNavTopInnerX = createNavSetter('setTopInnerX', 0);

/**
 * Set the nav topOuterY
 * @private For layout internal use
 */
export const useNavTopOuterY = createNavSetter('setTopOuterY', 0);

export default createNavSetter;
